import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShareNetwork, Copy, Check, X, DownloadSimple, TwitterLogo, LinkedinLogo, WhatsappLogo, InstagramLogo } from '@phosphor-icons/react';

/**
 * ShareButton - Drop-in share button for any generated content.
 * @param {string} title - Title of the content (for cards)
 * @param {string} content - Text content to share
 * @param {string} type - Type of content (for card label)
 */
export function ShareButton({ title, content, type = 'Content' }) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareText = `${title ? title + '\n\n' : ''}${typeof content === 'string' ? content : JSON.stringify(content, null, 2)}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || type, text: shareText + '\n\nMade with ScalitOS', url: shareUrl });
        return;
      } catch {}
    }
    setShowModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText + '\n\nMade with ScalitOS');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent((shareText).slice(0, 250) + '...\n\nMade with @ScalitOS');
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareToLinkedIn = () => {
    const text = encodeURIComponent(shareUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${text}`, '_blank');
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(shareText.slice(0, 500) + '\n\nMade with ScalitOS');
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const downloadAsCard = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1350;

    // Background
    ctx.fillStyle = '#080808';
    ctx.fillRect(0, 0, 1080, 1350);

    // Accent line
    ctx.fillStyle = '#c8f060';
    ctx.fillRect(60, 80, 4, 60);

    // Type label
    ctx.fillStyle = '#c8f060';
    ctx.font = '600 18px DM Sans, sans-serif';
    ctx.fillText(type.toUpperCase(), 80, 108);

    // Title
    ctx.fillStyle = '#f5f0e8';
    ctx.font = '48px serif';
    const titleLines = wrapText(ctx, title || type, 80, 190, 920, 56);

    // Content
    ctx.fillStyle = 'rgba(245,240,232,0.6)';
    ctx.font = '22px DM Sans, sans-serif';
    const contentText = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    const yStart = 190 + titleLines * 56 + 40;
    wrapText(ctx, contentText.slice(0, 800), 80, yStart, 920, 32);

    // Branding footer
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(0, 1260, 1080, 90);
    ctx.fillStyle = 'rgba(245,240,232,0.2)';
    ctx.font = '16px DM Sans, sans-serif';
    ctx.fillText('Made with ScalitOS', 80, 1310);

    // Download
    const link = document.createElement('a');
    link.download = `scalitos-${type.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <>
      <button onClick={handleShare} data-testid="share-content-btn"
        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs transition-colors"
        style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <ShareNetwork size={12} />Share
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-sm mx-4 rounded-2xl overflow-hidden"
              style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}
              onClick={e => e.stopPropagation()}
              data-testid="share-modal"
            >
              <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                <h3 className="font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Share</h3>
                <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
              </div>

              {/* Card preview */}
              <div className="p-5">
                <div className="rounded-xl p-5 mb-4" style={{ background: '#080808', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#c8f060' }}>{type}</span>
                  {title && <p className="text-sm font-medium mt-1 mb-2 line-clamp-2" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>{title}</p>}
                  <p className="text-xs line-clamp-4" style={{ color: 'rgba(245,240,232,0.5)' }}>
                    {typeof content === 'string' ? content : JSON.stringify(content).slice(0, 200)}
                  </p>
                  <p className="text-[10px] mt-3" style={{ color: 'rgba(245,240,232,0.15)' }}>Made with ScalitOS</p>
                </div>

                {/* Share actions */}
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={copyToClipboard} data-testid="share-copy-link"
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors"
                    style={{ background: 'rgba(255,255,255,0.05)', color: copied ? '#c8f060' : 'rgba(245,240,232,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    {copied ? <><Check size={14} />Copied!</> : <><Copy size={14} />Copy Text</>}
                  </button>
                  <button onClick={downloadAsCard} data-testid="share-download-card"
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <DownloadSimple size={14} />Download Card
                  </button>
                  <button onClick={shareToTwitter} data-testid="share-twitter"
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <TwitterLogo size={14} />Twitter / X
                  </button>
                  <button onClick={shareToLinkedIn} data-testid="share-linkedin"
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <LinkedinLogo size={14} />LinkedIn
                  </button>
                  <button onClick={shareToWhatsApp} data-testid="share-whatsapp"
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors col-span-2"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <WhatsappLogo size={14} />WhatsApp
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let lines = 0;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line, x, y);
      line = words[i] + ' ';
      y += lineHeight;
      lines++;
      if (lines > 15) break;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, y);
  return lines + 1;
}
